import ClearIcon from '@mui/icons-material/Clear';
import {
  Alert,
  Autocomplete,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2';
import { SetStateAction, useContext, useEffect, useState } from 'react';

import axios, { AxiosResponse } from 'axios';

import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';

import SpeakerNotesIcon from '@mui/icons-material/SpeakerNotes';
import { OrderType } from '../../../interfaces/delivery-company/orders';
import { isMobile, isTablet } from 'react-device-detect';
import { API_ENDPOINT } from '../../../configurations/global.config';
import { AuthContext } from '../../../contexts/user-context/user-context';
import { emptyCustomer } from '../../../data/customers';
import { Workflow, DeliveryState } from '../../../enums/orders';
import { CustomerType } from '../../../interfaces/common/customer';
import { ILocation } from '../../../interfaces/common/location';
import {
  MerchantType,
  CompactMerchantType,
} from '../../../interfaces/delivery-company/merchants';
import { ShopLocationType } from '../../../interfaces/delivery-company/pickups';
import { get_cod, get_price } from '../../../utils/cod';
import { Money } from '../../../utils/money';
import CreateBranchForm from '../../delivery-company-components/branches/create-branch-form/create-branch-form';
import CreateLocationForm from '../../delivery-company-components/location-form/location-form';
import CreateMerchantForm from '../../delivery-company-components/merchants/merchant-forms/create-or-edit-merchant';
import { emptyOrder } from '../../delivery-company-components/orders/create-order-form/create-order-form.utils';
import CreatePickupLocationForm from '../../delivery-company-components/pickup-location-form/pickup-location-form';
import AutocompleteWithThrottle from '../autocomplete-with-throttle/autocomplete-with-throttle';
import {
  assignMerchantProvider,
  assignBranchProvider,
  assignPickupLocationProvider,
  assignLocationProvider,
} from '../create-callbacks-utils/create-callbacks-utils';
import DimensionsInput from '../dimensions-input/dimensions-input';
import { ErrorsFlattenner } from '../errors-flattenner/errors-flattenner';
import InputWithLegend from '../input-with-legend/input-with-legend';
import LabelsInput from '../labels-input/labels-input';
import TextFieldWithScan from '../textfield-with-scan/textfield-with-scan';
import WeightInput from '../weight-input/weight-input';
import { IPaymentTask } from '../../../interfaces/delivery-company/tasks';
import { emptyPaymentTask } from '../../../data/paymenttasks';
import { DriverType } from '../../../interfaces/delivery-company/drivers';
import { IMerchantOrdersInvoice } from '../../../interfaces/common/merchant-invoices';
import { displayLocation } from '../../../utils/locations';

interface CreatePaymentTaskProps {
  invoice: IMerchantOrdersInvoice;
  onCreated?: (task: IPaymentTask) => void;
}

function CreatePaymentTask({ invoice, onCreated }: CreatePaymentTaskProps) {
  const { user, config } = useContext(AuthContext);

  const [paymentTask, setPaymentTask] = useState<IPaymentTask | undefined>(
    () => {
      return {
        ...emptyPaymentTask,
        invoice: invoice.id,
      };
    }
  );
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [loadingMessage, setLoadingMessage] = useState<string>('');
  const [response, setResponse] = useState<AxiosResponse>();
  const [message, setMessage] = useState<string>('');

  const handleFetchPickupLocations = async (query: string) => {
    if (invoice.merchant != null) {
      const endpoint = `${API_ENDPOINT}/api/v1/pickups/?page_size=10&merchant=${invoice.merchant.id}&name__contains=${encodeURIComponent(query)}`;

      try {
        const response = await axios.get<{
          count: number;
          results: ShopLocationType[];
        }>(endpoint, config);
        return response.data.results;
      } catch (error) {
        console.log(error);
      }
    }

    return [];
  };

  const handleFetchDrivers = async (query: string): Promise<DriverType[]> => {
    const endpoint = `${API_ENDPOINT}/api/v1/drivers/?page_size=10&name_or_phone=${encodeURIComponent(query)}`;

    try {
      const response = await axios.get<{
        count: number;
        results: DriverType[];
      }>(endpoint, config);

      return response.data.results;
    } catch (error) {
      console.log(error);
    }

    return [];
  };

  const handleSubmit = async (e: any) => {
    e.preventDefault();
    if (paymentTask) {
      setIsLoading(true);
      setResponse(undefined);
      setLoadingMessage('Scheduling Payment...');
      setMessage('');

      const requestData = paymentTask;

      try {
        const response = await axios.post<IPaymentTask>(
          `${API_ENDPOINT}/api/v1/payment-tasks/`,
          requestData,
          config
        );
        setResponse(response);
        setMessage('Payment scheduled successfully');
        onCreated && onCreated(response.data);
      } catch (e: any) {
        if (axios.isAxiosError(e)) {
          console.log('Response from catch block: ', response);
          setResponse(e.response);

          if (e.response !== undefined)
            setMessage('Failed to schedule payment: ' + e.response.statusText);
          else setMessage('Failed to schedule payment');
        } else {
          setMessage('Failed to schedule payment');
        }
        console.log(e);
      }
      setIsLoading(false);
      setLoadingMessage('');
    }
  };

  return (
    <>
      <Typography
        variant="h3"
        component="h3"
        sx={{ textAlign: 'center', mt: 3, mb: 3 }}
      >
        Schedule Payment
      </Typography>

      {isLoading ? (
        <>
          {loadingMessage}
          <br />
          <LinearProgress />
        </>
      ) : (
        <>
          {message != '' && (
            <Alert
              severity={
                response && response.status >= 200 && response.status < 300
                  ? 'success'
                  : 'error'
              }
            >
              <>
                {message}

                {response && response.status >= 300 && (
                  <ErrorsFlattenner rawErrors={response.data} />
                )}
              </>
            </Alert>
          )}

          {paymentTask && invoice.merchant != null && (
            <>
              <form onSubmit={handleSubmit}>
                <InputWithLegend
                  label={'Payment To'}
                  key="pay-to"
                  icon={<LocationOnIcon color="primary" />}
                >
                  <Grid container spacing={2} key="merchant">
                    <Grid xs={12} md={12}>
                      <>
                        <b>Merchant:</b> {invoice.merchant.name}{' '}
                        {invoice.merchant.phone_number}
                      </>
                      <br />
                      <>
                        <b>Invoice #{invoice.id}</b> -{' '}
                        {new Date(invoice.created_at).toLocaleString()}
                      </>
                    </Grid>

                    <Grid xs={12} md={12}>
                      <AutocompleteWithThrottle
                        label={'Payment Location'}
                        fetchAllOnLoad
                        value={paymentTask.merchant_location}
                        onChange={(e, value) =>
                          setPaymentTask({
                            ...paymentTask,
                            merchant_location: value,
                          })
                        }
                        fetch={handleFetchPickupLocations}
                        getOptionLabel={function (
                          option: ShopLocationType
                        ): string {
                          return `${option.name}: ${displayLocation(option)}`;
                        }}
                      />
                    </Grid>
                  </Grid>
                </InputWithLegend>

                <br />

                <AutocompleteWithThrottle
                  label={'Assign Driver'}
                  required={false}
                  fetch={handleFetchDrivers}
                  value={paymentTask.driver}
                  getOptionLabel={(option: DriverType) =>
                    option.name + ' ' + option.phone_number
                  }
                  onChange={(e, value) => {
                    setPaymentTask({ ...paymentTask, driver: value });
                  }}
                />

                {/* <FormControl fullWidth>
                                    <TextField
autoComplete="off"
                                        label="Note"
                                        type="text"
                                        variant="outlined"
                                        multiline
                                        value={paymentTask.note}
                                        rows={2}
                                        onChange={(e) =>
                                            setOrderData({
                                                ...orderData,
                                                public_note: e.target.value,
                                            })
                                        }
                                    />
                                </FormControl>
                                <br />
                                <br />
                                <FormControl fullWidth>
                                    <TextField
autoComplete="off"
                                        label="Internal Note"
                                        type="text"
                                        variant="outlined"
                                        multiline
                                        value={paymentTask.driver_notes?.join(" | ")}
                                        rows={2}
                                        onChange={(e) =>
                                            setOrderData({
                                                ...orderData,
                                                private_note: e.target.value,
                                            })
                                        }
                                    />
                                </FormControl> */}
                <br />
                <br />
                <div style={{ width: '100%', textAlign: 'center' }}>
                  <Button
                    size={(isMobile && !isTablet) ? 'small' : 'medium'}
                    type="submit"
                    variant="contained"
                  >
                    Schedule Payment
                  </Button>
                </div>
              </form>
            </>
          )}
        </>
      )}
    </>
  );
}

export default CreatePaymentTask;
